import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { Menu, X } from "lucide-react";
import { Logo } from "../brand/Logo";
import { useAuth } from "../../../lib/auth/useAuth";

const NAV = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
];

export function PublicLayout() {
  const { session } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const primaryCtaHref = session ? "/inbox" : "/login";
  const primaryCtaLabel = session ? "Open app" : "Get started free";

  return (
    <div className="min-h-screen bg-white text-[var(--ink-900)] flex flex-col">
      <header
        className={[
          "sticky top-0 z-40 w-full transition-all duration-200 border-b",
          scrolled
            ? "bg-white/85 backdrop-blur-md border-[var(--ink-200)]"
            : "bg-transparent border-transparent",
        ].join(" ")}
      >
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-5 md:px-8 h-24 md:h-16">
          <div className="flex items-center gap-10">
            <Link to="/" aria-label="Proofly home" className="flex items-center">
              <Logo size={36} />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      "px-3 py-2 text-sm rounded-md transition-colors",
                      isActive
                        ? "text-[var(--ink-900)]"
                        : "text-[var(--ink-700)] hover:text-[var(--ink-900)]",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-2">
            {!session && (
              <Link
                to="/login"
                className="px-3 py-2 text-sm font-medium text-[var(--ink-700)] hover:text-[var(--ink-900)]"
              >
                Sign in
              </Link>
            )}
            <Link
              to={primaryCtaHref}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--ink-950)] hover:bg-black text-white text-sm font-medium transition-colors"
            >
              {primaryCtaLabel}
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden p-3 -mr-3 rounded-md text-[var(--ink-700)] hover:bg-[var(--ink-100)]"
          >
            {menuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-[var(--ink-200)] bg-white">
            <div className="max-w-[1200px] mx-auto px-5 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      "px-3 py-2.5 text-base rounded-md",
                      isActive
                        ? "bg-[var(--ink-100)] text-[var(--ink-900)]"
                        : "text-[var(--ink-700)] hover:bg-[var(--ink-100)]",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="h-px bg-[var(--ink-200)] my-2" />
              {!session && (
                <Link
                  to="/login"
                  className="px-3 py-2.5 text-base rounded-md text-[var(--ink-700)] hover:bg-[var(--ink-100)]"
                >
                  Sign in
                </Link>
              )}
              <Link
                to={primaryCtaHref}
                className="mx-3 mt-2 inline-flex items-center justify-center h-10 rounded-full bg-[var(--ink-950)] hover:bg-black text-white text-sm font-medium"
              >
                {primaryCtaLabel}
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--ink-200)] bg-[var(--ink-50)]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14 grid gap-10 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
        <div>
          <Logo size={28} />
          <p className="mt-4 max-w-xs text-sm text-[var(--ink-700)]">
            Audit-ready financial records for small businesses. Free forever
            for teams under 10.
          </p>
          <p className="mt-4 text-xs text-[var(--ink-500)]">proofly.one</p>
        </div>
        <FooterColumn
          title="Product"
          links={[
            { label: "Features", to: "/features" },
            { label: "Pricing", to: "/pricing" },
            { label: "Sign in", to: "/login" },
          ]}
        />
        <FooterColumn
          title="Company"
          links={[
            { label: "Get started", to: "/login" },
            { label: "Contact sales", to: "mailto:hello@proofly.one", external: true },
          ]}
        />
        <FooterColumn
          title="Legal"
          links={[
            { label: "Terms", to: "/terms" },
            { label: "Privacy", to: "/privacy" },
            { label: "Security", to: "/privacy#security" },
          ]}
        />
      </div>
      <div className="border-t border-[var(--ink-200)]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-6 flex flex-wrap gap-3 items-center justify-between text-xs text-[var(--ink-500)]">
          <span>© {new Date().getFullYear()} Proofly, Inc. All rights reserved.</span>
          <span>Made for small businesses that want to stay ready.</span>
        </div>
      </div>
    </footer>
  );
}

type FooterLink = { label: string; to: string; external?: boolean };

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--ink-500)] mb-3">
        {title}
      </div>
      <ul className="space-y-2">
        {links.map((link) =>
          link.external ? (
            <li key={link.label}>
              <a
                href={link.to}
                className="text-sm text-[var(--ink-700)] hover:text-[var(--ink-900)]"
              >
                {link.label}
              </a>
            </li>
          ) : (
            <li key={link.label}>
              <Link
                to={link.to}
                className="text-sm text-[var(--ink-700)] hover:text-[var(--ink-900)]"
              >
                {link.label}
              </Link>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
